import { DESTINATIONS, type DestinationId } from '@/components/visitone/navigation-model'
import { ACCESS_PATH, getBffBaseUrl, getVisitKey } from '@/lib/bff/config'

/*
 * BFF client for the caller's access decision in one tenant (`getAccess`,
 * VIS-329, ADR-052). Identity resolves it from the caller's roles in the
 * tenant; the SPA renders only from it and never authorises from the token.
 * Anything the client does not recognise is dropped, so an unexpected
 * response can only hide things, never show them.
 */

export type DestinationMode = 'change' | 'view'

/** `AccessDecision` in `openapi/bff-openapi.yaml`. */
export type Access = {
  tenant: string
  /** Permitted destinations only, in rail order. */
  destinations: { id: DestinationId; mode: DestinationMode }[]
  /** Action ids, e.g. `guests.change` or the principal action `landlords.manage`. */
  actions: string[]
  /** Plan-limited feature ids the caller's roles permit and the tenant's plan includes. Empty in V1. */
  features: { id: string }[]
}

/** Raised for a non-2xx response, a network failure or a malformed body; `status` is 0 when no response arrived. */
export class AccessRequestError extends Error {
  readonly status: number
  readonly code: string | null

  constructor(status: number, code: string | null, message = `Access request failed (${status})`) {
    super(message)
    this.name = 'AccessRequestError'
    this.status = status
    this.code = code
  }
}

const RAIL_ORDER = new Map(DESTINATIONS.map((destination, index) => [destination.id, index]))

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object'

function normalise(data: unknown, tenantId: string): Access {
  if (
    !isRecord(data) ||
    data.tenant !== tenantId ||
    !Array.isArray(data.destinations) ||
    !Array.isArray(data.actions) ||
    !Array.isArray(data.features)
  ) {
    throw new AccessRequestError(200, null, 'Unexpected access response shape')
  }

  const destinations = data.destinations
    .filter(
      (entry): entry is { id: DestinationId; mode: DestinationMode } =>
        isRecord(entry) &&
        typeof entry.id === 'string' &&
        RAIL_ORDER.has(entry.id as DestinationId) &&
        (entry.mode === 'change' || entry.mode === 'view'),
    )
    .map(({ id, mode }) => ({ id, mode }))
    .filter((entry, index, all) => all.findIndex((other) => other.id === entry.id) === index)
    .sort((a, b) => (RAIL_ORDER.get(a.id) ?? 0) - (RAIL_ORDER.get(b.id) ?? 0))

  const features = data.features
    .filter((entry): entry is { id: string } => isRecord(entry) && typeof entry.id === 'string')
    .map(({ id }) => ({ id }))
    .filter((entry, index, all) => all.findIndex((other) => other.id === entry.id) === index)

  return {
    tenant: tenantId,
    destinations,
    actions: data.actions.filter((action): action is string => typeof action === 'string'),
    features,
  }
}

/** The caller's access decision in `tenantId`. Rejects with `AccessRequestError` on any failure. */
export async function fetchAccess(token: string, tenantId: string, signal?: AbortSignal): Promise<Access> {
  const url = `${getBffBaseUrl('mmv')}${ACCESS_PATH}?${new URLSearchParams({ tenant: tenantId })}`

  let response: Response
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'x-visit-key': getVisitKey(),
        Accept: 'application/json',
      },
      signal,
    })
  } catch {
    throw new AccessRequestError(0, null, 'Network error while loading access')
  }

  if (!response.ok) {
    let code: string | null = null
    try {
      const body = (await response.json()) as { error?: unknown }
      if (typeof body?.error === 'string') code = body.error
    } catch {
      code = null
    }
    throw new AccessRequestError(response.status, code)
  }

  let data: unknown
  try {
    data = await response.json()
  } catch {
    throw new AccessRequestError(response.status, null, 'Malformed access response')
  }
  return normalise(data, tenantId)
}
